export interface CustomerAddressOption {
    label: string;
    addressId: string;
    full: any;
}

export interface ResolvedCustomerAddress {
    address: string;
    addressId: string;
    customerAddress: string;
    addressCountryCode: string;
    isCustom: number;
    customAddress: string;
}

export type ResolveAddressResult =
    | { ok: true; resolved: ResolvedCustomerAddress }
    | { ok: false; message: string };

function formatAddressLabel(addr: any): string {
    return [addr?.addressLine1, addr?.addressLine2, addr?.addressLine3, addr?.addressLine4]
        .filter((line) => !!line && String(line).trim() !== '' && String(line).trim() !== '.')
        .map((line) => String(line).trim())
        .join(', ');
}

/** Map SearchCustomer/Details response addresses to picker options. */
export function mapCustomerAddressesFromDetails(details: any): CustomerAddressOption[] {
    if (!details) {
        return [];
    }

    let rawAddresses: any[] = [];
    if (Array.isArray(details.addresses) && details.addresses.length > 0) {
        rawAddresses = details.addresses;
    } else if (Array.isArray(details) && details.length > 0) {
        rawAddresses = details;
    } else if (details.addressLine1) {
        rawAddresses = [details];
    }

    return rawAddresses
        .map((addr, index) => {
            const label = formatAddressLabel(addr);
            if (!label) {
                return null;
            }
            const addressId = String(addr?.addressId ?? '').trim() || `__index_${index}`;
            return { label, addressId, full: addr } as CustomerAddressOption;
        })
        .filter((opt): opt is CustomerAddressOption => !!opt);
}

function validateCustomAddressLines(raw: string): { valid: boolean; message?: string } {
    const lines = (raw || '')
        .split(/\r?\n|\||,/)
        .map((line) => line.trim())
        .filter((line) => !!line);

    if (lines.length === 0) {
        return { valid: false, message: 'Please enter at least one address line.' };
    }

    const maxAddressLines = 4;
    if (lines.length > maxAddressLines) {
        return {
            valid: false,
            message: `Only ${maxAddressLines} address lines are allowed.`
        };
    }

    const maxCharsPerLine = 36;
    for (let i = 0; i < lines.length; i++) {
        if (lines[i].length > maxCharsPerLine) {
            return {
                valid: false,
                message: `Address line ${i + 1} exceeds ${maxCharsPerLine} characters.`
            };
        }
    }
    return { valid: true };
}

function normalizeCustomAddressPipe(raw: string): string {
    const lines = (raw || '')
        .split(/\r?\n|\||,/)
        .map((line) => line.trim())
        .filter((line) => !!line);
    return lines.join('|');
}

function getAddressInputText(value: string | CustomerAddressOption | null | undefined): string {
    if (value == null) {
        return '';
    }
    if (typeof value === 'object' && 'label' in value) {
        return String(value.label || '').trim();
    }
    return String(value).trim();
}

/** Resolve list selection or custom typed address (same rules as Manage Orders Proceed). */
export function resolveCustomOrListAddress(
    value: string | CustomerAddressOption | null | undefined,
    options: CustomerAddressOption[],
    currentAddressId?: string
): ResolveAddressResult {
    const text = getAddressInputText(value);
    if (!text) {
        return { ok: false, message: 'Please select an address from the list or enter a custom delivery address.' };
    }

    const selectedObject = value != null && typeof value === 'object' && 'label' in value ? value : null;
    const byId = currentAddressId ? options.find((a) => a.addressId === currentAddressId) : undefined;
    const byLabel = options.find((a) => a.label === text);
    const fromSelection = selectedObject ?? (byId && byId.label === text ? byId : byLabel && byLabel.label === text ? byLabel : undefined);

    if (fromSelection) {
        const realAddressId = String(fromSelection.full?.addressId ?? '').trim();
        return {
            ok: true,
            resolved: {
                address: fromSelection.label,
                addressId: realAddressId,
                customerAddress: fromSelection.full?.combinedAddress || '',
                addressCountryCode: fromSelection.full?.countryCode || '',
                isCustom: 0,
                customAddress: ''
            }
        };
    }

    const customAddressValidation = validateCustomAddressLines(text);
    if (!customAddressValidation.valid) {
        return {
            ok: false,
            message: customAddressValidation.message || 'Each address line must be 36 characters or less.'
        };
    }

    return {
        ok: true,
        resolved: {
            address: text,
            addressId: '',
            customerAddress: '',
            addressCountryCode: '',
            isCustom: 1,
            customAddress: normalizeCustomAddressPipe(text)
        }
    };
}

/** Apply resolved address fields onto item and product. */
export function applySelectedCustomerAddress(item: any, product: any, resolved: ResolvedCustomerAddress): void {
    if (!item) {
        return;
    }
    item.address = resolved.address;
    item.addressId = resolved.addressId;
    item.customerAddress = resolved.customerAddress;
    item.addressCountryCode = resolved.addressCountryCode;
    item.isCustom = resolved.isCustom;
    item.customAddress = resolved.customAddress;

    if (product) {
        product.address = resolved.address;
        product.addressId = resolved.addressId;
        product.customerAddress = resolved.customerAddress;
        product.addressCountryCode = resolved.addressCountryCode;
        product.isCustom = resolved.isCustom;
        product.customAddress = resolved.customAddress;
    }
}
